import {
    CircularProgress,
    Card,
    CardContent,
    Typography,
    Button,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Box,
} from "@mui/material";
import { useAppContext } from "app/AppContext";
import React, { useState } from "react";
import FiltrosAutomatizadorFrente from "./FiltrosAutomatizadorFrente";
import TabelaLaudosAutomatizador from "./TabelaLaudosAutomatizador";
import SeletorConfiguracao from "./SeletorConfiguracao";
import SeletorConfiguracaoStatus from "./SeletorConfiguracaoStatus";
import SeletorConfiguracaoEquipamento from "./SeletorConfiguracaoEquipamento";
import AdicionarAcimaAbaixoUnico from "./AdicionarAcimaAbaixoUnico";
import LaudoVisita from "entity/LaudoVisita";

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`automatizador-tabpanel-${index}`}
            aria-labelledby={`automatizador-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

export default function AutomatizadorUnificado() {
    const { useSetTitle } = useAppContext();
    const [laudos, setLaudos] = useState([]);
    const [laudosSelecionados, setLaudosSelecionados] = useState([]);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [tipoAtualizacao, setTipoAtualizacao] = useState(0); // 0 = Frente, 1 = Status, 2 = Equipamento

    const [configuracaoFrente, setConfiguracaoFrente] = useState({
        tipoIndicador: "",
        indicadores: [],
        condicao: "",
        valorCondicao: "",
        novoValorFrente: "",
    });

    const [configuracaoEquipamento, setConfiguracaoEquipamento] = useState({
        tipoIndicador: "",
        indicadores: [],
        condicao: "",
        valorCondicao: "",
        novoEquipamento: null,
    });

    const [configuracaoStatus, setConfiguracaoStatus] = useState({
        statusAtual: "",
        novoStatus: "",
        condicoes: [],
    });

    const [processando, setProcessando] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useSetTitle("Assistente de Atualização de Laudos");

    const handleAtualizarFrente = async () => {
        if (!configuracaoFrente.novoValorFrente) {
            setSnackbar({
                open: true,
                message: "Configure o novo valor da frente",
                severity: "warning",
            });
            return;
        }

        setProcessando(true);
        try {
            let totalApontamentosAtualizados = 0;

            const promises = laudosSelecionados.map(async (laudo) => {
                const apontamentos = laudo.apontamentos;
                if (!apontamentos) return 0;

                let laudoModificado = false;
                let apontamentosAtualizadosLaudo = 0;
                const novoApontamentos = { ...apontamentos };

                if (apontamentos[configuracaoFrente.tipoIndicador]) {
                    Object.keys(apontamentos[configuracaoFrente.tipoIndicador]).forEach((indicadorKey) => {
                        if (!configuracaoFrente.indicadores.includes(indicadorKey)) {
                            return;
                        }

                        const indicadorApontamentos = apontamentos[configuracaoFrente.tipoIndicador][indicadorKey];

                        if (Array.isArray(indicadorApontamentos)) {
                            indicadorApontamentos.forEach((apontamento, index) => {
                                let deveAtualizar = false;

                                // Verificar condição de atualização
                                switch (configuracaoFrente.condicao) {
                                    case "todos":
                                        deveAtualizar = true;
                                        break;
                                    case "raa":
                                        deveAtualizar =
                                            apontamento.frente && apontamento.frente.trim().toLowerCase() === "raa";
                                        break;
                                    case "vazio":
                                        deveAtualizar = !apontamento.frente || apontamento.frente.trim() === "";
                                        break;
                                    case "custom":
                                        deveAtualizar =
                                            apontamento.frente &&
                                            apontamento.frente.trim().toLowerCase() ===
                                                configuracaoFrente.valorCondicao?.toLowerCase();
                                        break;
                                    default:
                                        deveAtualizar = false;
                                }

                                if (deveAtualizar) {
                                    novoApontamentos[configuracaoFrente.tipoIndicador][indicadorKey][index] = {
                                        ...apontamento,
                                        frente: configuracaoFrente.novoValorFrente,
                                    };
                                    laudoModificado = true;
                                    apontamentosAtualizadosLaudo++;
                                }
                            });
                        }
                    });
                }

                if (laudoModificado) {
                    const entity = new LaudoVisita();
                    await entity.update({
                        uid: laudo.uid,
                        apontamentos: novoApontamentos,
                    });
                    await entity.save();
                    return apontamentosAtualizadosLaudo;
                }
                return 0;
            });

            const resultados = await Promise.all(promises);
            totalApontamentosAtualizados = resultados.reduce((sum, count) => sum + count, 0);

            setSnackbar({
                open: true,
                message: `${laudosSelecionados.length} laudo(s) processado(s) com ${totalApontamentosAtualizados} apontamentos atualizados!`,
                severity: "success",
            });

            setLaudosSelecionados([]);
            setRefreshTrigger((prev) => prev + 1);
        } catch (error) {
            console.error("Erro ao atualizar laudos:", error);
            setSnackbar({
                open: true,
                message: "Erro ao atualizar laudos. Tente novamente.",
                severity: "error",
            });
        } finally {
            setProcessando(false);
        }
    };

    const handleAtualizarEquipamento = async () => {
        if (!configuracaoEquipamento.novoEquipamento) {
            setSnackbar({
                open: true,
                message: "Configure o novo equipamento",
                severity: "warning",
            });
            return;
        }

        setProcessando(true);
        try {
            let totalApontamentosAtualizados = 0;

            const promises = laudosSelecionados.map(async (laudo) => {
                const apontamentos = laudo.apontamentos;
                if (!apontamentos) return 0;

                let laudoModificado = false;
                let apontamentosAtualizadosLaudo = 0;
                const novoApontamentos = { ...apontamentos };

                if (apontamentos[configuracaoEquipamento.tipoIndicador]) {
                    Object.keys(apontamentos[configuracaoEquipamento.tipoIndicador]).forEach((indicadorKey) => {
                        if (!configuracaoEquipamento.indicadores.includes(indicadorKey)) {
                            return;
                        }

                        const indicadorApontamentos = apontamentos[configuracaoEquipamento.tipoIndicador][indicadorKey];
                        console.log("indicadorApontamentos ", indicadorApontamentos);

                        if (Array.isArray(indicadorApontamentos)) {
                            indicadorApontamentos.forEach((apontamento, index) => {
                                let deveAtualizar = false;

                                // Verificar condição de atualização
                                switch (configuracaoEquipamento.condicao) {
                                    case "todos":
                                        deveAtualizar = true;
                                        break;
                                    case "vazio":
                                        deveAtualizar =
                                            !apontamento.equipamento ||
                                            (typeof apontamento.equipamento === "string" &&
                                                apontamento.equipamento.trim() === "") ||
                                            (typeof apontamento.equipamento === "object" &&
                                                !apontamento.equipamento.equipamento);
                                        break;
                                    case "custom":
                                        if (typeof apontamento.equipamento === "string") {
                                            deveAtualizar =
                                                apontamento.equipamento &&
                                                apontamento.equipamento.trim().toLowerCase() ===
                                                    configuracaoEquipamento.valorCondicao?.toLowerCase();
                                        } else if (typeof apontamento.equipamento === "object") {
                                            deveAtualizar =
                                                apontamento.equipamento?.equipamento &&
                                                apontamento.equipamento.equipamento.trim().toLowerCase() ===
                                                    configuracaoEquipamento.valorCondicao?.toLowerCase();
                                        }
                                        break;
                                    default:
                                        deveAtualizar = false;
                                }

                                if (deveAtualizar) {
                                    novoApontamentos[configuracaoEquipamento.tipoIndicador][indicadorKey][index] = {
                                        ...apontamento,
                                        equipamento: {
                                            equipamento: configuracaoEquipamento.novoEquipamento.equipamento,
                                            uid: configuracaoEquipamento.novoEquipamento.uid,
                                        },
                                    };
                                    laudoModificado = true;
                                    apontamentosAtualizadosLaudo++;
                                }
                            });
                        }
                    });
                }

                if (laudoModificado) {
                    const entity = new LaudoVisita();
                    await entity.update({
                        uid: laudo.uid,
                        apontamentos: novoApontamentos,
                    });
                    await entity.save();
                    return apontamentosAtualizadosLaudo;
                }
                return 0;
            });

            const resultados = await Promise.all(promises);
            totalApontamentosAtualizados = resultados.reduce((sum, count) => sum + count, 0);

            setSnackbar({
                open: true,
                message: `${laudosSelecionados.length} laudo(s) processado(s) com ${totalApontamentosAtualizados} apontamentos atualizados!`,
                severity: "success",
            });

            setLaudosSelecionados([]);
            setRefreshTrigger((prev) => prev + 1);
        } catch (error) {
            console.error("Erro ao atualizar laudos:", error);
            setSnackbar({
                open: true,
                message: "Erro ao atualizar laudos. Tente novamente.",
                severity: "error",
            });
        } finally {
            setProcessando(false);
        }
    };

    const handleAtualizarStatus = async () => {
        if (!configuracaoStatus.novoStatus) {
            setSnackbar({
                open: true,
                message: "Configure o novo status",
                severity: "warning",
            });
            return;
        }

        setProcessando(true);
        try {
            const promises = laudosSelecionados.map(async (laudo) => {
                let deveAtualizar = true;

                if (configuracaoStatus.statusAtual && laudo.status !== configuracaoStatus.statusAtual) {
                    deveAtualizar = false;
                }

                if (deveAtualizar) {
                    const entity = new LaudoVisita();
                    await entity.update({
                        uid: laudo.uid,
                        status: configuracaoStatus.novoStatus,
                    });
                    await entity.save();
                    return 1;
                }
                return 0;
            });

            const resultados = await Promise.all(promises);
            const totalAtualizados = resultados.reduce((sum, count) => sum + count, 0);

            setSnackbar({
                open: true,
                message: `${totalAtualizados} laudo(s) com status atualizado!`,
                severity: "success",
            });

            setLaudosSelecionados([]);
            setRefreshTrigger((prev) => prev + 1);
        } catch (error) {
            console.error("Erro ao atualizar status:", error);
            setSnackbar({
                open: true,
                message: "Erro ao atualizar status. Tente novamente.",
                severity: "error",
            });
        } finally {
            setProcessando(false);
        }
    };

    const handleAtualizar = () => {
        if (laudosSelecionados.length === 0) {
            setSnackbar({
                open: true,
                message: "Selecione pelo menos um laudo para atualizar",
                severity: "warning",
            });
            return;
        }

        if (tipoAtualizacao === 0) {
            handleAtualizarFrente();
        } else if (tipoAtualizacao === 1) {
            handleAtualizarStatus();
        } else if (tipoAtualizacao === 2) {
            handleAtualizarEquipamento();
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const isConfigValid = () => {
        if (tipoAtualizacao === 0) {
            return configuracaoFrente.novoValorFrente && configuracaoFrente.indicadores.length > 0;
        } else if (tipoAtualizacao === 1) {
            return configuracaoStatus.novoStatus;
        } else if (tipoAtualizacao === 2) {
            return configuracaoEquipamento.novoEquipamento && configuracaoEquipamento.indicadores.length > 0;
        }
        return false;
    };

    const getButtonText = () => {
        let tipoText = "";
        if (tipoAtualizacao === 0) {
            tipoText = "Frente";
        } else if (tipoAtualizacao === 1) {
            tipoText = "Status";
        } else if (tipoAtualizacao === 2) {
            tipoText = "Equipamento";
        }
        return `Atualizar ${tipoText} - ${laudosSelecionados.length} Laudo(s)`;
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <Card sx={{ m: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Adicionar Acima/Abaixo por Período
                    </Typography>
                    <AdicionarAcimaAbaixoUnico />
                </CardContent>
            </Card>

            <FiltrosAutomatizadorFrente
                laudos={laudos}
                setLaudos={setLaudos}
                globalLoading={globalLoading}
                setGlobalLoading={setGlobalLoading}
                refreshTrigger={refreshTrigger}
            />

            {laudos && laudos.length > 0 && (
                <>
                    <Card sx={{ m: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Configuração da Atualização
                            </Typography>

                            <Tabs
                                value={tipoAtualizacao}
                                onChange={(_, newValue) => setTipoAtualizacao(newValue)}
                                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                            >
                                <Tab label="Atualizar Frente" />
                                <Tab label="Atualizar Status" />
                                <Tab label="Atualizar Equipamento" />
                            </Tabs>

                            <TabPanel value={tipoAtualizacao} index={0}>
                                <SeletorConfiguracao
                                    configuracao={configuracaoFrente}
                                    setConfiguracao={setConfiguracaoFrente}
                                />
                            </TabPanel>

                            <TabPanel value={tipoAtualizacao} index={1}>
                                <SeletorConfiguracaoStatus
                                    configuracao={configuracaoStatus}
                                    setConfiguracao={setConfiguracaoStatus}
                                />
                            </TabPanel>

                            <TabPanel value={tipoAtualizacao} index={2}>
                                <SeletorConfiguracaoEquipamento
                                    configuracao={configuracaoEquipamento}
                                    setConfiguracao={setConfiguracaoEquipamento}
                                />
                            </TabPanel>

                            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "16px" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAtualizar}
                                    disabled={processando || !isConfigValid() || laudosSelecionados.length === 0}
                                >
                                    {processando ? <CircularProgress size={20} /> : getButtonText()}
                                </Button>
                            </div>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                {laudosSelecionados.length} de {laudos.length} laudo(s) selecionado(s)
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card sx={{ m: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Laudos Encontrados ({laudos.length})
                            </Typography>
                            {globalLoading ? (
                                <div style={{ textAlign: "center", padding: "20px" }}>
                                    <CircularProgress />
                                </div>
                            ) : (
                                <TabelaLaudosAutomatizador
                                    laudos={laudos}
                                    laudosSelecionados={laudosSelecionados}
                                    setLaudosSelecionados={setLaudosSelecionados}
                                />
                            )}
                        </CardContent>
                    </Card>
                </>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
